(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-yds-room-visualiser]';
  let segmenterPromise = null;

  async function getSegmenter() {
    if (!segmenterPromise) {
      segmenterPromise = import('https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.8.1/+esm')
        .then(({ pipeline }) => pipeline('image-segmentation', 'Xenova/segformer-b0-finetuned-ade-512-512'))
        .catch((error) => {
          segmenterPromise = null;
          throw error;
        });
    }
    return segmenterPromise;
  }

  function toCm(value, unit) {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    const u = String(unit || 'cm').toLowerCase();
    if (u === 'mm') return n / 10;
    if (u === 'm') return n * 100;
    return n;
  }

  function parseDimensions(title, shape) {
    const text = String(title || '').replace(/×/g, 'x');

    // Only trust dimensions when the variant explicitly says which side is high/wide.
    // This prevents ambiguous labels such as “42cm x 59cm Set” being interpreted backwards.
    const highWide = text.match(/([0-9]+(?:\.[0-9]+)?)\s*(mm|cm|m)\s*(?:high|h)\s*x\s*([0-9]+(?:\.[0-9]+)?)\s*(mm|cm|m)\s*(?:wide|w)/i);
    if (highWide) {
      return {
        heightCm: toCm(highWide[1], highWide[2]),
        widthCm: toCm(highWide[3], highWide[4])
      };
    }

    const wideHigh = text.match(/([0-9]+(?:\.[0-9]+)?)\s*(mm|cm|m)\s*(?:wide|w)\s*x\s*([0-9]+(?:\.[0-9]+)?)\s*(mm|cm|m)\s*(?:high|h)/i);
    if (wideHigh) {
      return {
        widthCm: toCm(wideHigh[1], wideHigh[2]),
        heightCm: toCm(wideHigh[3], wideHigh[4])
      };
    }

    const diameter = text.match(/([0-9]+(?:\.[0-9]+)?)\s*(mm|cm|m)\s*diameter/i);
    if (diameter) {
      const d = toCm(diameter[1], diameter[2]);
      return { widthCm: d, heightCm: d };
    }

    // Mural dots are circular, so a single size such as “60cm” or “1.2m” is safely a diameter.
    if (shape === 'circle') {
      const single = text.match(/^\s*([0-9]+(?:\.[0-9]+)?)\s*(mm|cm|m)(?:\s|$|-)/i);
      if (single) {
        const d = toCm(single[1], single[2]);
        return { widthCm: d, heightCm: d };
      }
    }

    // An unlabelled square is orientation-independent and safe to use.
    const square = text.match(/([0-9]+(?:\.[0-9]+)?)\s*(mm|cm|m)\s*x\s*([0-9]+(?:\.[0-9]+)?)\s*(mm|cm|m)/i);
    if (square) {
      const a = toCm(square[1], square[2]);
      const b = toCm(square[3], square[4]);
      if (Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) <= 0.5) return { widthCm: a, heightCm: b };
    }

    return { widthCm: null, heightCm: null };
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function prepareWhiteBackgroundArtwork(sourceUrl, shape) {
    return new Promise((resolve, reject) => {
      if (!sourceUrl) {
        reject(new Error('No artwork source URL'));
        return;
      }

      const source = new Image();
      source.crossOrigin = 'anonymous';
      source.decoding = 'async';

      source.onload = () => {
        try {
          const maxSide = 1800;
          const naturalW = source.naturalWidth || source.width;
          const naturalH = source.naturalHeight || source.height;
          if (!naturalW || !naturalH) throw new Error('Artwork image has no dimensions');

          const scale = Math.min(1, maxSide / Math.max(naturalW, naturalH));
          const width = Math.max(1, Math.round(naturalW * scale));
          const height = Math.max(1, Math.round(naturalH * scale));
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (!ctx) throw new Error('Canvas is unavailable');
          ctx.drawImage(source, 0, 0, width, height);

          const imageData = ctx.getImageData(0, 0, width, height);
          const data = imageData.data;
          const pixelCount = width * height;

          // Average small patches in all four corners. This is more tolerant of JPEG compression
          // than assuming the background is exactly RGB 255/255/255.
          let br = 0; let bg = 0; let bb = 0; let samples = 0;
          const patch = Math.max(2, Math.min(10, Math.floor(Math.min(width, height) * 0.012)));
          const corners = [
            [0, 0], [Math.max(0, width - patch), 0],
            [0, Math.max(0, height - patch)], [Math.max(0, width - patch), Math.max(0, height - patch)]
          ];
          corners.forEach(([sx, sy]) => {
            for (let y = sy; y < Math.min(height, sy + patch); y += 1) {
              for (let x = sx; x < Math.min(width, sx + patch); x += 1) {
                const i = (y * width + x) * 4;
                if (data[i + 3] < 8) continue;
                br += data[i]; bg += data[i + 1]; bb += data[i + 2]; samples += 1;
              }
            }
          });
          if (!samples) throw new Error('Could not sample artwork background');
          br /= samples; bg /= samples; bb /= samples;

          // This batch rollout is deliberately conservative: only process images whose outside
          // background is genuinely white / near-white. Lifestyle photos and coloured backgrounds
          // fail here and the visualiser stays hidden for that product.
          const cornerMin = Math.min(br, bg, bb);
          const cornerMax = Math.max(br, bg, bb);
          if (cornerMin < 220 || (cornerMax - cornerMin) > 36) {
            throw new Error('Artwork does not have a white outside background');
          }

          const isBackground = (pixelIndex, relaxed) => {
            const i = pixelIndex * 4;
            if (data[i + 3] < 8) return true;
            const r = data[i]; const g = data[i + 1]; const b = data[i + 2];
            const dr = r - br; const dg = g - bg; const db = b - bb;
            const distance = Math.sqrt((dr * dr) + (dg * dg) + (db * db));
            const minChannel = Math.min(r, g, b);
            const maxChannel = Math.max(r, g, b);
            const neutral = (maxChannel - minChannel) <= (relaxed ? 28 : 20);
            return minChannel >= (relaxed ? 228 : 238) && neutral && distance <= (relaxed ? 48 : 32);
          };

          // Flood only from the outside edge. White details enclosed inside the artwork therefore
          // stay visible instead of being erased simply because they are white.
          const seen = new Uint8Array(pixelCount);
          const queue = new Int32Array(pixelCount);
          let head = 0; let tail = 0;
          const add = (index) => {
            if (index < 0 || index >= pixelCount || seen[index] || !isBackground(index, true)) return;
            seen[index] = 1;
            queue[tail] = index;
            tail += 1;
          };

          for (let x = 0; x < width; x += 1) {
            add(x);
            add((height - 1) * width + x);
          }
          for (let y = 1; y < height - 1; y += 1) {
            add(y * width);
            add(y * width + width - 1);
          }

          while (head < tail) {
            const index = queue[head]; head += 1;
            const i = index * 4;
            data[i + 3] = 0;
            const x = index % width;
            if (x > 0) add(index - 1);
            if (x < width - 1) add(index + 1);
            if (index >= width) add(index - width);
            if (index < pixelCount - width) add(index + width);
          }

          const removedFraction = tail / Math.max(pixelCount, 1);
          if (removedFraction < 0.025) {
            throw new Error('Not enough white background was found around the artwork');
          }

          // Remove a tiny near-white JPEG fringe beside the transparent edge. Two passes are enough
          // to clean the halo without aggressively eating into pale artwork.
          for (let pass = 0; pass < 2; pass += 1) {
            const toClear = [];
            for (let y = 1; y < height - 1; y += 1) {
              for (let x = 1; x < width - 1; x += 1) {
                const index = y * width + x;
                const a = data[index * 4 + 3];
                if (a < 8 || !isBackground(index, false)) continue;
                const neighbours = [index - 1, index + 1, index - width, index + width];
                if (neighbours.some((n) => data[n * 4 + 3] < 8)) toClear.push(index);
              }
            }
            toClear.forEach((index) => { data[index * 4 + 3] = 0; });
            if (!toClear.length) break;
          }

          let minX = width; let minY = height; let maxX = -1; let maxY = -1;
          for (let y = 0; y < height; y += 1) {
            for (let x = 0; x < width; x += 1) {
              if (data[(y * width + x) * 4 + 3] < 8) continue;
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
          if (maxX < minX || maxY < minY) throw new Error('Artwork could not be separated from its background');

          // A small padding protects anti-aliased artwork edges. For mural dots the physical product
          // is a circle, so force a square crop around the detected circle: its cropped width is then
          // exactly the physical diameter used by the Shopify variant.
          const padding = 3;
          minX = Math.max(0, minX - padding);
          minY = Math.max(0, minY - padding);
          maxX = Math.min(width - 1, maxX + padding);
          maxY = Math.min(height - 1, maxY + padding);

          if (shape === 'circle') {
            const boxW = maxX - minX + 1;
            const boxH = maxY - minY + 1;
            let size = Math.max(boxW, boxH);
            const cx = (minX + maxX) / 2;
            const cy = (minY + maxY) / 2;
            size = Math.min(size, width, height);
            minX = Math.round(cx - size / 2);
            minY = Math.round(cy - size / 2);
            minX = clamp(minX, 0, Math.max(0, width - size));
            minY = clamp(minY, 0, Math.max(0, height - size));
            maxX = minX + size - 1;
            maxY = minY + size - 1;
          }

          ctx.putImageData(imageData, 0, 0);
          const cropW = maxX - minX + 1;
          const cropH = maxY - minY + 1;
          const cropped = document.createElement('canvas');
          cropped.width = cropW;
          cropped.height = cropH;
          const cropCtx = cropped.getContext('2d');
          if (!cropCtx) throw new Error('Crop canvas is unavailable');
          cropCtx.drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);

          resolve({
            url: cropped.toDataURL('image/png'),
            originalWidth: width,
            originalHeight: height,
            croppedWidth: cropW,
            croppedHeight: cropH
          });
        } catch (error) {
          reject(error);
        }
      };

      source.onerror = () => reject(new Error('Artwork source could not be loaded for automatic preparation'));
      source.src = sourceUrl;
    });
  }

  function init(root) {
    if (!root || root.dataset.ydsRvReady === 'true') return;
    root.dataset.ydsRvReady = 'true';

    const modal = root.querySelector('[data-yds-rv-modal]');
    const openBtn = root.querySelector('[data-yds-rv-open]');
    const closeBtns = root.querySelectorAll('[data-yds-rv-close]');
    const empty = root.querySelector('[data-yds-rv-empty]');
    const stageWrap = root.querySelector('[data-yds-rv-stage-wrap]');
    const viewerColumn = root.querySelector('.yds-rv__viewer-column');
    const stage = root.querySelector('[data-yds-rv-stage]');
    const roomImg = root.querySelector('[data-yds-rv-room]');
    const artwork = root.querySelector('[data-yds-rv-artwork]');
    const occlusionCanvas = root.querySelector('[data-yds-rv-occlusion]');
    const controls = root.querySelector('[data-yds-rv-controls]');
    const footer = root.querySelector('[data-yds-rv-footer]');
    const fileInputs = root.querySelectorAll('[data-yds-rv-file], [data-yds-rv-file-secondary]');
    const sizes = root.querySelector('[data-yds-rv-sizes]');
    const selectedSize = root.querySelector('[data-yds-rv-selected-size]');
    const price = root.querySelector('[data-yds-rv-price]');
    const footerSize = root.querySelector('[data-yds-rv-footer-size]');
    const scaleInput = root.querySelector('[data-yds-rv-scale]');
    const rotateInput = root.querySelector('[data-yds-rv-rotate]');
    const resetBtn = root.querySelector('[data-yds-rv-reset]');
    const nudgeBtns = root.querySelectorAll('[data-yds-rv-nudge]');
    const knownDistanceInput = root.querySelector('[data-yds-rv-known-distance]');
    const startScaleBtn = root.querySelector('[data-yds-rv-start-scale]');
    const clearScaleBtn = root.querySelector('[data-yds-rv-clear-scale]');
    const scaleStatus = root.querySelector('[data-yds-rv-scale-status]');
    const calibrationHint = root.querySelector('[data-yds-rv-calibration-hint]');
    const measureLine = root.querySelector('[data-yds-rv-measure-line]');
    const measurePoints = Array.from(root.querySelectorAll('[data-yds-rv-point]'));
    const chooseBtn = root.querySelector('[data-yds-rv-choose]');
    const cartBtn = root.querySelector('[data-yds-rv-cart]');
    const detectBtn = root.querySelector('[data-yds-rv-detect]');
    const aiStatus = root.querySelector('[data-yds-rv-ai-status]');
    const removePhotoBtn = root.querySelector('[data-yds-rv-remove-photo]');
    const roomNameInput = root.querySelector('[data-yds-rv-room-name]');
    const planningNoteInput = root.querySelector('[data-yds-rv-planning-note]');
    const saveBtn = root.querySelector('[data-yds-rv-save]');
    const saveStatus = root.querySelector('[data-yds-rv-save-status]');
    const variantDataNode = root.querySelector('[data-yds-rv-variants]');
    const candidateDataNode = root.querySelector('[data-yds-rv-artwork-candidates]');

    if (!modal || !openBtn || !stage || !artwork || !variantDataNode) return;

    function setRoomLoadedState(hasRoom) {
      modal.classList.toggle('yds-rv__modal--has-room', !!hasRoom);
      modal.classList.toggle('yds-rv__modal--awaiting-room', !hasRoom);
    }
    setRoomLoadedState(false);

    const productFormId = root.dataset.productFormId || '';
    const artworkProcessing = root.dataset.artworkProcessing || 'none';
    const artworkShape = root.dataset.artworkShape || 'freeform';
    const artworkSourceUrl = root.dataset.artworkUrl || artwork.currentSrc || artwork.src || '';
    const productTitle = root.dataset.productTitle || 'YDS Studio wall art';
    const logoUrl = root.dataset.logoUrl || '';

    let variants = [];
    try {
      variants = JSON.parse(variantDataNode.textContent || '[]').map((v) => {
        const display = parseDimensions(v.title, artworkShape);
        if (root.dataset.productHandle === 'auckland-silhouette-wall-decal') {
          v.title = v.options[0];
          if (v.options[0].startsWith('2.64 m')) display.widthCm = 264.2918302200647;
          display.heightCm = display.widthCm / 2.2024319185005394;
        }
        const hasCanvas = Number.isFinite(v.renderWidthCm) && Number.isFinite(v.renderHeightCm) && v.renderWidthCm > 0 && v.renderHeightCm > 0;
        return {
          ...v,
          id: String(v.id),
          ...display,
          ...(hasCanvas ? { displayWidthCm: display.widthCm, widthCm: v.renderWidthCm, heightCm: v.renderHeightCm } : {})
        };
      }).filter((v) => {
        if (!Number.isFinite(v.widthCm) || !Number.isFinite(v.heightCm)) return false;
        // Catch catalogue typos such as “87m wide” rather than letting them create a wildly wrong mockup.
        return v.widthCm >= 5 && v.heightCm >= 5 && v.widthCm <= (root.dataset.productHandle === 'whale-mother-calf-wall-decal' ? 1000 : 600) && v.heightCm <= 600;
      });
    } catch (err) {
      console.warn('YDS room visualiser: variant data could not be read.', err);
    }

    if (!variants.length) {
      root.remove();
      return;
    }

    const state = {
      roomUrl: '',
      roomFile: null,
      aiRunning: false,
      aiApplied: false,
      scaleSource: null,
      x: 50,
      y: 50,
      widthPct: 30,
      rotation: 0,
      selectedVariantId: variants[0] ? variants[0].id : '',
      calibrationMode: false,
      calibrationPoints: [],
      calibrated: false,
      calibrationDistanceCm: null,
      calibrationDistanceWidthFraction: null,
      dragPointer: null,
      dragStartX: 0,
      dragStartY: 0,
      dragStartStateX: 50,
      dragStartStateY: 50,
      lastFocus: null
    };

    let artworkReadyPromise = Promise.resolve();
    let logoReadyPromise = Promise.resolve(null);

    if (logoUrl) {
      logoReadyPromise = new Promise((resolve) => {
        const logo = new Image();
        logo.crossOrigin = 'anonymous';
        logo.onload = () => resolve(logo);
        logo.onerror = () => resolve(null);
        logo.src = logoUrl;
      });
    }

    if (artworkProcessing === 'auto-trim-white') {
      artwork.style.opacity = '0';
      let artworkCandidates = [artworkSourceUrl].filter(Boolean);
      if (candidateDataNode) {
        try {
          const parsed = JSON.parse(candidateDataNode.textContent || '[]');
          if (Array.isArray(parsed) && parsed.length) artworkCandidates = parsed.filter(Boolean);
        } catch (error) {
          console.warn('YDS room visualiser: artwork candidate list could not be read.', error);
        }
      }

      artworkReadyPromise = (async () => {
        let lastError = null;
        for (const candidateUrl of artworkCandidates) {
          try {
            const prepared = await prepareWhiteBackgroundArtwork(candidateUrl, artworkShape);

            // QA guard for freeform wall decals: after the white canvas is trimmed, the artwork's
            // visible aspect ratio must closely match the physical product proportions. This catches
            // detail crops or a wrong standalone image even when the file lives on the correct product.
            if (artworkShape !== 'circle') {
              const physicalRatios = variants
                .map((v) => (Number.isFinite(v.widthCm) && Number.isFinite(v.heightCm) && v.heightCm > 0 ? v.widthCm / v.heightCm : null))
                .filter((v) => Number.isFinite(v));
              if (physicalRatios.length) {
                const sorted = physicalRatios.slice().sort((a, b) => a - b);
                const targetRatio = sorted[Math.floor(sorted.length / 2)];
                const cropRatio = prepared.croppedWidth / Math.max(prepared.croppedHeight, 1);
                const ratioError = Math.max(cropRatio / targetRatio, targetRatio / cropRatio);
                if (!Number.isFinite(ratioError) || ratioError > 1.15) {
                  throw new Error('Prepared artwork proportions do not match the product dimensions');
                }
              }
            }

            artwork.src = prepared.url;
            artwork.style.opacity = '';
            root.dataset.ydsRvArtworkPrepared = 'true';
            root.dataset.ydsRvArtworkSource = candidateUrl;
            root.hidden = false;
            return prepared;
          } catch (error) {
            lastError = error;
          }
        }
        throw lastError || new Error('No verified white-background artwork image was found');
      })().catch((error) => {
        console.warn('YDS room visualiser: no verified white-background artwork was found for this product.', error);
        if (modal && modal.parentElement) modal.remove();
        root.remove();
        return null;
      });
    } else {
      root.hidden = false;
    }

    // Move the dialog out of theme stacking contexts so the site header can never cover it.
    if (modal.parentElement !== document.body) document.body.appendChild(modal);

    function getProductForm() {
      if (productFormId) {
        const byId = document.getElementById(productFormId);
        if (byId) return byId;
      }
      return document.querySelector(`form[data-product-handle="${CSS.escape(root.dataset.productHandle || '')}"]`) ||
        document.querySelector('form[data-product-form]');
    }

    function getCurrentFormVariantId() {
      const form = getProductForm();
      const input = form && form.querySelector('input[name="id"]');
      return input && input.value ? String(input.value) : '';
    }

    function selectedVariant() {
      return variants.find((v) => v.id === state.selectedVariantId) || variants[0] || null;
    }

    function manualWidthForVariant(variant) {
      if (!variant || !Number.isFinite(variant.widthCm)) return 30;
      return clamp((variant.widthCm / 120) * 38, 14, 82);
    }

    function applyRealScale() {
      const variant = selectedVariant();
      if (!state.calibrated || !variant || !Number.isFinite(variant.widthCm) || !state.calibrationDistanceCm || !state.calibrationDistanceWidthFraction) return false;
      const widthFraction = (variant.widthCm / state.calibrationDistanceCm) * state.calibrationDistanceWidthFraction;
      state.widthPct = clamp(widthFraction * 100, 4, 95);
      return true;
    }

    function renderArtwork() {
      artwork.style.left = `${state.x}%`;
      artwork.style.top = `${state.y}%`;
      artwork.style.width = `${state.widthPct}%`;
      artwork.style.transform = `translate(-50%, -50%) rotate(${state.rotation}deg)`;
      scaleInput.value = String(Math.round(state.widthPct));
      rotateInput.value = String(Math.round(state.rotation));
      scaleInput.disabled = state.calibrated;
    }

    function renderCalibration() {
      const rect = stage.getBoundingClientRect();
      const pts = state.calibrationPoints;

      measurePoints.forEach((node, index) => {
        const p = pts[index];
        if (!p) {
          node.hidden = true;
          return;
        }
        node.hidden = false;
        node.style.left = `${p.x * 100}%`;
        node.style.top = `${p.y * 100}%`;
      });

      if (pts.length === 2 && rect.width > 0 && rect.height > 0) {
        const x1 = pts[0].x * rect.width;
        const y1 = pts[0].y * rect.height;
        const x2 = pts[1].x * rect.width;
        const y2 = pts[1].y * rect.height;
        const dx = x2 - x1;
        const dy = y2 - y1;
        const distance = Math.hypot(dx, dy);
        const angle = Math.atan2(dy, dx) * 180 / Math.PI;
        measureLine.hidden = false;
        measureLine.style.left = `${pts[0].x * 100}%`;
        measureLine.style.top = `${pts[0].y * 100}%`;
        measureLine.style.width = `${distance}px`;
        measureLine.style.transform = `rotate(${angle}deg)`;
      } else {
        measureLine.hidden = true;
      }

      stage.classList.toggle('is-calibrating', state.calibrationMode);
      calibrationHint.hidden = !state.calibrationMode;
      clearScaleBtn.hidden = !(state.calibrated || pts.length);

      if (state.calibrated) {
        const variant = selectedVariant();
        const physicalWidth = variant && (variant.displayWidthCm || variant.widthCm);
        const widthLabel = root.dataset.sizeUnits === 'mixed' && physicalWidth >= 100
          ? `${Number((physicalWidth / 100).toFixed(2))} m`
          : `${Math.round(physicalWidth)} cm`;
        const name = physicalWidth ? `${widthLabel} wide${variant.displayWidthCm ? ' whale pair' : ''}` : 'selected size';
        scaleStatus.textContent = state.scaleSource === 'auto'
          ? `Estimated using a 2.4m floor-to-ceiling height for the ${name}. Use the measurement tool for better accuracy.`
          : `Measurement guide active for the ${name}. Perspective in your photo can still affect appearance.`;
      } else if (state.calibrationMode) {
        scaleStatus.textContent = pts.length === 0 ? 'Tap the first end of your measurement on the photo.' : 'Now tap the second end.';
      } else {
        scaleStatus.textContent = '';
      }
    }

    function renderSizes() {
      sizes.innerHTML = '';
      variants.filter(v => root.dataset.productHandle !== 'auckland-silhouette-wall-decal' || v.options[1] === selectedVariant()?.options[1]).forEach((variant) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `yds-rv__size-btn${variant.id === state.selectedVariantId ? ' is-selected' : ''}`;
        btn.disabled = !variant.available;
        btn.dataset.variantId = variant.id;
        const main = document.createElement('span');
        main.textContent = variant.title;
        const small = document.createElement('small');
        small.textContent = variant.available ? variant.price : 'Sold out';
        btn.append(main, small);
        btn.addEventListener('click', () => selectVariant(variant.id, true));
        sizes.appendChild(btn);
      });

      const variant = selectedVariant();
      if (variant) {
        selectedSize.textContent = state.calibrated ? (state.scaleSource === 'auto' ? 'Shown using an estimated 2.4m ceiling height.' : 'Shown using your real-world measurement.') : 'Size is approximate until you use the measurement tool.';
        price.textContent = variant.price || '';
        footerSize.textContent = variant.title;
        cartBtn.disabled = !variant.available;
      }
    }

    function selectVariant(id, fromUser) {
      const next = variants.find((v) => v.id === String(id));
      if (!next) return;
      state.selectedVariantId = next.id;
      if (state.calibrated) applyRealScale();
      else if (fromUser) state.widthPct = manualWidthForVariant(next);
      renderSizes();
      renderArtwork();
      renderCalibration();
    }

    function resetPlacement() {
      state.x = 50;
      state.y = 50;
      state.rotation = 0;
      if (state.calibrated) applyRealScale();
      else state.widthPct = manualWidthForVariant(selectedVariant());
      renderArtwork();
    }

    function fitStageToViewer() {
      if (!stage || !roomImg || !roomImg.naturalWidth || !roomImg.naturalHeight) return;

      if (!window.matchMedia('(min-width: 800px)').matches || !viewerColumn || stageWrap.hidden) {
        stage.style.width = '';
        stage.style.height = '';
        return;
      }

      const styles = window.getComputedStyle(viewerColumn);
      const padX = (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
      const padY = (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
      const availableW = Math.max(220, viewerColumn.clientWidth - padX);
      const availableH = Math.max(220, viewerColumn.clientHeight - padY - 34); // room-photo hint
      const ratio = roomImg.naturalWidth / roomImg.naturalHeight;

      let fittedW = availableW;
      let fittedH = fittedW / ratio;
      if (fittedH > availableH) {
        fittedH = availableH;
        fittedW = fittedH * ratio;
      }

      stage.style.width = `${Math.round(fittedW)}px`;
      stage.style.height = `${Math.round(fittedH)}px`;
    }

    let aucklandMaskPromise;
    async function tintAucklandArtwork() {
      if (root.dataset.productHandle !== 'auckland-silhouette-wall-decal') return;
      const form = getProductForm();
      const fields = form ? new FormData(form) : null;
      const colour = fields && (fields.get('properties[_Colour HEX]') || fields.get('properties[Colour HEX]'));
      if (!/^#[0-9a-f]{6}$/i.test(colour || '')) return;
      if (!aucklandMaskPromise) aucklandMaskPromise = new Promise((resolve, reject) => {
        const image = new Image(); image.crossOrigin = 'anonymous'; image.onload = () => resolve(image); image.onerror = reject; image.src = artworkSourceUrl;
      });
      try {
        const image = await aucklandMaskPromise, canvas = document.createElement('canvas');
        canvas.width = 2000; canvas.height = Math.round(2000 * image.naturalHeight / image.naturalWidth);
        const ctx = canvas.getContext('2d'); ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        ctx.globalCompositeOperation = 'source-in'; ctx.fillStyle = colour; ctx.fillRect(0, 0, canvas.width, canvas.height);
        artwork.src = canvas.toDataURL('image/png'); await artwork.decode();
      } catch (error) { console.warn('Auckland colour preview could not be prepared.', error); }
    }
    function openModal() {
      tintAucklandArtwork();
      state.lastFocus = document.activeElement;
      if (!state.roomUrl || !roomImg.getAttribute('src')) setRoomLoadedState(false);
      const current = getCurrentFormVariantId();
      if (current && variants.some((v) => v.id === current)) selectVariant(current, false);
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('yds-rv-lock');
      requestAnimationFrame(() => {
        fitStageToViewer();
        renderCalibration();
      });
      setTimeout(() => {
        const close = modal.querySelector('.yds-rv__close');
        if (close) close.focus();
      }, 0);
    }

    function closeModal() {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('yds-rv-lock');
      if (state.lastFocus && typeof state.lastFocus.focus === 'function') state.lastFocus.focus();
    }

    function loadRoom(file) {
      if (!file || !file.type || !file.type.startsWith('image/')) {
        scaleStatus.textContent = 'Please choose an image file.';
        return;
      }
      if (state.roomUrl) URL.revokeObjectURL(state.roomUrl);
      state.roomFile = file;
      state.roomUrl = URL.createObjectURL(file);
      clearRoomAnalysis();
      roomImg.onload = () => {
        setRoomLoadedState(true);
        empty.hidden = true;
        stageWrap.hidden = false;
        controls.hidden = false;
        footer.hidden = false;
        clearCalibration();
        resetPlacement();
        requestAnimationFrame(() => {
          fitStageToViewer();
          renderCalibration();
        });
        roomImg.onload = null;
      };
      roomImg.onerror = () => {
        scaleStatus.textContent = 'That image format could not be displayed. Try a JPG, PNG or WebP photo.';
      };
      roomImg.src = state.roomUrl;
    }

    function startCalibration() {
      const known = Number(knownDistanceInput.value);
      if (!Number.isFinite(known) || known < 10 || known > 2000) {
        scaleStatus.textContent = 'Enter a measurement between 10 cm and 2000 cm.';
        knownDistanceInput.focus();
        return;
      }
      state.calibrationMode = true;
      state.calibrationPoints = [];
      state.calibrated = false;
      state.scaleSource = null;
      state.calibrationDistanceCm = known;
      state.calibrationDistanceWidthFraction = null;
      renderCalibration();
    }

    function completeCalibration() {
      if (state.calibrationPoints.length !== 2) return;
      const rect = stage.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const a = state.calibrationPoints[0];
      const b = state.calibrationPoints[1];
      const dx = (b.x - a.x) * rect.width;
      const dy = (b.y - a.y) * rect.height;
      const distancePx = Math.hypot(dx, dy);
      if (distancePx < 20) {
        state.calibrationPoints = [];
        scaleStatus.textContent = 'Those points are too close together. Please mark the measurement again.';
        renderCalibration();
        return;
      }
      state.calibrationDistanceWidthFraction = distancePx / rect.width;
      state.calibrated = true;
      state.scaleSource = 'manual';
      state.calibrationMode = false;
      applyRealScale();
      renderArtwork();
      renderSizes();
      renderCalibration();
    }

    function clearCalibration() {
      state.calibrationMode = false;
      state.calibrationPoints = [];
      state.calibrated = false;
      state.scaleSource = null;
      state.calibrationDistanceCm = null;
      state.calibrationDistanceWidthFraction = null;
      renderCalibration();
      renderSizes();
      renderArtwork();
    }

    function maskValue(mask, x, y) {
      const xx = clamp(Math.round(x), 0, mask.width - 1);
      const yy = clamp(Math.round(y), 0, mask.height - 1);
      const channels = mask.channels || 1;
      return mask.data[(yy * mask.width + xx) * channels] || 0;
    }

    function findBestWallPoint(mask) {
      const w = mask.width;
      const h = mask.height;
      if (!w || !h) return null;
      const stepX = Math.max(6, Math.floor(w / 42));
      const stepY = Math.max(6, Math.floor(h / 32));
      const radiusX = Math.max(12, Math.floor(w * 0.075));
      const radiusY = Math.max(12, Math.floor(h * 0.075));
      let best = null;

      for (let y = Math.floor(h * 0.12); y < h * 0.82; y += stepY) {
        for (let x = Math.floor(w * 0.08); x < w * 0.92; x += stepX) {
          if (maskValue(mask, x, y) < 128) continue;
          let wallHits = 0;
          let samples = 0;
          for (let sy = -2; sy <= 2; sy += 1) {
            for (let sx = -2; sx <= 2; sx += 1) {
              const px = x + (sx / 2) * radiusX;
              const py = y + (sy / 2) * radiusY;
              samples += 1;
              if (maskValue(mask, px, py) >= 128) wallHits += 1;
            }
          }
          const wallRatio = wallHits / Math.max(samples, 1);
          const centrePenalty = Math.hypot((x / w) - 0.5, (y / h) - 0.43) * 0.10;
          const score = wallRatio - centrePenalty;
          if (!best || score > best.score) best = { x, y, score, wallRatio };
        }
      }
      return best && best.wallRatio >= 0.56 ? best : null;
    }

    function estimateWallHeight(mask, point) {
      if (!point) return null;
      const w = mask.width;
      const h = mask.height;
      const strip = Math.max(2, Math.floor(w * 0.012));
      const rowIsWall = (y) => {
        let hits = 0;
        let total = 0;
        for (let x = point.x - strip; x <= point.x + strip; x += Math.max(1, Math.floor(strip / 3))) {
          total += 1;
          if (maskValue(mask, x, y) >= 128) hits += 1;
        }
        return hits / Math.max(total, 1) >= 0.55;
      };

      let top = point.y;
      let misses = 0;
      for (let y = point.y; y >= 0; y -= 2) {
        if (rowIsWall(y)) { top = y; misses = 0; }
        else if (++misses >= 4) break;
      }
      let bottom = point.y;
      misses = 0;
      for (let y = point.y; y < h; y += 2) {
        if (rowIsWall(y)) { bottom = y; misses = 0; }
        else if (++misses >= 4) break;
      }
      const span = Math.max(0, bottom - top);
      if (span < h * 0.46) return null;
      return { top, bottom, span };
    }

    function paintOcclusion(wallMask) {
      if (!occlusionCanvas || !wallMask || !roomImg.naturalWidth || !roomImg.naturalHeight) return;
      const w = wallMask.width;
      const h = wallMask.height;
      occlusionCanvas.width = w;
      occlusionCanvas.height = h;
      const ctx = occlusionCanvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(roomImg, 0, 0, w, h);
      const imageData = ctx.getImageData(0, 0, w, h);
      const channels = wallMask.channels || 1;
      for (let i = 0; i < w * h; i += 1) {
        const wall = wallMask.data[i * channels] || 0;
        // Transparent on wall; original room remains in front everywhere else.
        imageData.data[i * 4 + 3] = wall >= 128 ? 0 : 255;
      }
      ctx.putImageData(imageData, 0, 0);
      occlusionCanvas.hidden = false;
      state.aiApplied = true;
    }

    function clearRoomAnalysis() {
      state.aiApplied = false;
      if (occlusionCanvas) {
        occlusionCanvas.hidden = true;
        const ctx = occlusionCanvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, occlusionCanvas.width, occlusionCanvas.height);
      }
      if (detectBtn) {
        detectBtn.disabled = false;
        detectBtn.textContent = 'Detect wall & furniture';
      }
      if (aiStatus) aiStatus.textContent = 'Runs on your device. Your room photo is not uploaded.';
    }

    async function runRoomAnalysis(isAutomatic) {
      if (!state.roomFile || state.aiRunning || !detectBtn || !aiStatus) return;
      state.aiRunning = true;
      detectBtn.disabled = true;
      detectBtn.textContent = 'Detecting…';
      aiStatus.textContent = 'Finding the wall and foreground objects… First use may take a little longer.';

      try {
        const segmenter = await getSegmenter();
        const maxAnalysisSide = 960;
        const sourceW = roomImg.naturalWidth || 1;
        const sourceH = roomImg.naturalHeight || 1;
        const analysisScale = Math.min(1, maxAnalysisSide / Math.max(sourceW, sourceH));
        const analysisW = Math.max(1, Math.round(sourceW * analysisScale));
        const analysisH = Math.max(1, Math.round(sourceH * analysisScale));
        const output = await segmenter(state.roomFile, {
          subtask: 'semantic',
          target_sizes: [[analysisH, analysisW]]
        });
        const segments = Array.isArray(output) ? output : [];
        const wall = segments.find((item) => String(item.label || '').toLowerCase() === 'wall');
        if (!wall || !wall.mask) throw new Error('No wall was detected');

        paintOcclusion(wall.mask);
        const best = findBestWallPoint(wall.mask);
        if (best) {
          state.x = clamp((best.x / wall.mask.width) * 100, 2, 98);
          state.y = clamp((best.y / wall.mask.height) * 100, 2, 98);
        }

        // If a substantial floor-to-ceiling wall span is visible, use 2.4m only as a quick estimate.
        // A manual measurement always takes precedence and remains the more accurate option.
        if (state.scaleSource !== 'manual' && best) {
          const height = estimateWallHeight(wall.mask, best);
          if (height) {
            state.calibrationDistanceCm = 240;
            state.calibrationDistanceWidthFraction = height.span / wall.mask.width;
            state.calibrated = true;
            state.scaleSource = 'auto';
            applyRealScale();
          }
        }

        renderArtwork();
        renderSizes();
        renderCalibration();
        aiStatus.textContent = state.scaleSource === 'auto'
          ? 'Wall detected. Furniture is kept in front. Size uses a 2.4m ceiling estimate—measure your wall before ordering.'
          : 'Wall detected. Furniture is kept in front. Use the measurement tool if you want a more accurate size.';
        detectBtn.textContent = 'Run detection again';
      } catch (error) {
        console.warn('YDS room visualiser: room detection unavailable.', error);
        clearRoomAnalysis();
        aiStatus.textContent = isAutomatic
          ? 'Automatic wall detection was not available on this device. You can still position the decal manually.'
          : 'Wall detection was not available. You can still position the decal manually.';
      } finally {
        state.aiRunning = false;
        detectBtn.disabled = false;
      }
    }

    function removeRoomPhoto() {
      if (state.roomUrl) URL.revokeObjectURL(state.roomUrl);
      state.roomUrl = '';
      state.roomFile = null;
      roomImg.removeAttribute('src');
      clearRoomAnalysis();
      clearCalibration();
      resetPlacement();
      setRoomLoadedState(false);
      stageWrap.hidden = true;
      controls.hidden = true;
      footer.hidden = true;
      empty.hidden = false;
    }

    function safeFilePart(value) {
      return String(value || '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .replace(/[^a-z0-9\-_. ]/gi, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 70) || 'room';
    }

    function canvasToBlob(canvas, type, quality) {
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('The room mockup could not be saved.'));
        }, type || 'image/jpeg', quality || 0.92);
      });
    }

    function fitCanvasFont(ctx, text, maxWidth, startSize, weight, family) {
      let size = startSize;
      while (size > 16) {
        ctx.font = `${weight || 400} ${size}px ${family}`;
        if (ctx.measureText(text).width <= maxWidth) break;
        size -= 2;
      }
      return size;
    }

    function wrapCanvasText(ctx, text, x, y, maxWidth, lineHeight, maxLines) {
      const words = String(text || '').trim().split(/\s+/).filter(Boolean);
      if (!words.length) return 0;
      const lines = [];
      let line = '';
      for (const word of words) {
        const test = line ? `${line} ${word}` : word;
        if (ctx.measureText(test).width > maxWidth && line) {
          lines.push(line);
          line = word;
          if (lines.length >= maxLines - 1) break;
        } else {
          line = test;
        }
      }
      if (line && lines.length < maxLines) lines.push(line);
      // If text was truncated, add an ellipsis to the final visible line.
      const joined = lines.join(' ');
      if (joined.length < String(text || '').trim().length && lines.length) {
        let last = lines[lines.length - 1];
        while (last.length > 1 && ctx.measureText(`${last}…`).width > maxWidth) last = last.slice(0, -1);
        lines[lines.length - 1] = `${last}…`;
      }
      lines.forEach((value, index) => ctx.fillText(value, x, y + (index * lineHeight), maxWidth));
      return lines.length;
    }

    async function buildMockupCanvas() {
      if (!state.roomFile || !roomImg.src || !roomImg.naturalWidth || !roomImg.naturalHeight) {
        throw new Error('Upload a room photo first.');
      }
      const variant = selectedVariant();
      if (!variant) throw new Error('Choose a product size first.');

      await artworkReadyPromise.catch(() => {});
      if (typeof artwork.decode === 'function') {
        try { await artwork.decode(); } catch (_) {}
      }
      const brandLogo = await logoReadyPromise.catch(() => null);

      const naturalW = roomImg.naturalWidth;
      const naturalH = roomImg.naturalHeight;
      const maxSide = 1800;
      const imageScale = Math.min(1, maxSide / Math.max(naturalW, naturalH));
      const roomW = Math.max(1, Math.round(naturalW * imageScale));
      const roomH = Math.max(1, Math.round(naturalH * imageScale));
      const roomName = roomNameInput ? roomNameInput.value.trim() : '';
      const planningNote = planningNoteInput ? planningNoteInput.value.trim().slice(0, 100) : '';
      const baseInfoH = Math.max(152, Math.round(roomW * 0.12));
      // Reserve enough footer height for the full 100-character planning note.
      const infoH = baseInfoH + (planningNote ? Math.max(112, Math.round(roomW * 0.07)) : 0);
      const canvas = document.createElement('canvas');
      canvas.width = roomW;
      canvas.height = roomH + infoH;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Your browser could not create the room mockup.');

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(roomImg, 0, 0, roomW, roomH);

      const artRatio = artwork.naturalWidth && artwork.naturalHeight ? artwork.naturalHeight / artwork.naturalWidth : 1;
      const artW = roomW * (state.widthPct / 100);
      const artH = artW * artRatio;
      const centreX = roomW * (state.x / 100);
      const centreY = roomH * (state.y / 100);
      ctx.save();
      ctx.translate(centreX, centreY);
      ctx.rotate((state.rotation || 0) * Math.PI / 180);
      ctx.drawImage(artwork, -artW / 2, -artH / 2, artW, artH);
      ctx.restore();

      if (state.aiApplied && occlusionCanvas && !occlusionCanvas.hidden && occlusionCanvas.width && occlusionCanvas.height) {
        ctx.drawImage(occlusionCanvas, 0, 0, roomW, roomH);
      }

      const bodyFont = 'Arial, Helvetica, sans-serif';
      const pad = Math.max(24, Math.round(roomW * 0.025));
      const infoTop = roomH;
      const metaSize = Math.max(15, Math.round(roomW * 0.013));
      const smallSize = Math.max(12, Math.round(roomW * 0.0105));
      const productMaxW = Math.max(260, roomW * 0.49);

      ctx.fillStyle = '#f7f7f3';
      ctx.fillRect(0, infoTop, roomW, infoH);
      ctx.fillStyle = '#111111';
      ctx.fillRect(0, infoTop, roomW, Math.max(2, Math.round(roomW * 0.0015)));
      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';

      const leftMaxW = Math.max(220, Math.round(roomW * 0.36));
      let leftY = infoTop + pad;
      if (brandLogo && brandLogo.naturalWidth && brandLogo.naturalHeight) {
        const maxLogoW = Math.min(leftMaxW, Math.round(roomW * 0.27));
        const maxLogoH = Math.max(34, Math.round(baseInfoH * 0.31));
        const logoScale = Math.min(maxLogoW / brandLogo.naturalWidth, maxLogoH / brandLogo.naturalHeight);
        const logoW = Math.round(brandLogo.naturalWidth * logoScale);
        const logoH = Math.round(brandLogo.naturalHeight * logoScale);
        ctx.drawImage(brandLogo, pad, leftY, logoW, logoH);
        leftY += logoH + 8;
      } else {
        const brandSize = Math.max(23, Math.round(roomW * 0.022));
        ctx.font = `700 ${brandSize}px ${bodyFont}`;
        ctx.fillText('YDS STUDIO', pad, leftY);
        leftY += brandSize + 8;
      }
      ctx.font = `400 ${metaSize}px ${bodyFont}`;
      ctx.fillText('www.yourdecalshop.co.nz', pad, leftY, leftMaxW);
      leftY += metaSize + 8;
      ctx.fillStyle = '#555555';
      ctx.font = `400 ${smallSize}px ${bodyFont}`;
      wrapCanvasText(ctx, 'Artwork © YDS Studio. All rights reserved.', pad, leftY, leftMaxW, Math.round(smallSize * 1.28), 2);

      ctx.fillStyle = '#111111';
      const rightX = Math.round(roomW * 0.48);
      const titleSize = fitCanvasFont(ctx, productTitle, productMaxW, Math.max(22, Math.round(roomW * 0.019)), 600, bodyFont);
      let rightY = infoTop + pad;
      ctx.font = `600 ${titleSize}px ${bodyFont}`;
      ctx.fillText(productTitle, rightX, rightY, productMaxW);
      rightY += titleSize + 8;
      ctx.font = `400 ${metaSize}px ${bodyFont}`;
      const details = [variant.title];
      if (roomName) details.push(`Room: ${roomName}`);
      ctx.fillText(details.join('  •  '), rightX, rightY, productMaxW);
      rightY += metaSize + 10;

      if (planningNote) {
        ctx.fillStyle = '#555555';
        ctx.font = `400 ${smallSize}px ${bodyFont}`;
        ctx.fillText('Planning note (reference only):', rightX, rightY, productMaxW);
        rightY += Math.round(smallSize * 1.35);
        ctx.fillStyle = '#111111';
        ctx.font = `400 ${metaSize}px ${bodyFont}`;
        wrapCanvasText(ctx, planningNote, rightX, rightY, productMaxW, Math.round(metaSize * 1.32), 4);
      }

      return canvas;
    }

    function mockupFilename(extension) {
      const variant = selectedVariant();
      const roomName = roomNameInput ? roomNameInput.value.trim() : '';
      const bits = ['YDS-Studio', roomName || 'Room', productTitle, variant ? variant.title : 'Mockup'].map(safeFilePart);
      return `${bits.join('-')}.${extension}`;
    }

    async function downloadMockup() {
      if (!saveBtn) return;
      const originalText = saveBtn.textContent;
      saveBtn.disabled = true;
      saveBtn.textContent = 'Preparing…';
      if (saveStatus) saveStatus.textContent = '';
      try {
        const canvas = await buildMockupCanvas();
        const blob = await canvasToBlob(canvas, 'image/jpeg', 0.92);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = mockupFilename('jpg');
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 30000);
        if (saveStatus) saveStatus.textContent = 'Mockup saved.';
      } catch (error) {
        console.warn('YDS room visualiser: mockup save failed.', error);
        if (saveStatus) saveStatus.textContent = error && error.message ? error.message : 'The mockup could not be saved.';
      } finally {
        saveBtn.disabled = false;
        saveBtn.textContent = originalText;
      }
    }

    async function buildMockupBlob() {
      const canvas = await buildMockupCanvas();
      return canvasToBlob(canvas, 'image/jpeg', 0.90);
    }

    function syncVariantToProductPage() {
      const variant = selectedVariant();
      const form = getProductForm();
      if (!variant || !form) return false;

      const optionValue = variant.options && variant.options[0] ? String(variant.options[0]) : variant.title;
      const formScope = form.closest('[data-section-id]') || document;
      const popoutOption = Array.from(formScope.querySelectorAll('[data-popout-option]')).find((el) => String(el.dataset.value || '') === optionValue);
      const controlsForOption = Array.from(document.querySelectorAll(`[form="${CSS.escape(form.id)}"][name^="options"]`));
      let optionControl = controlsForOption.find((el) => String(el.value) === optionValue);
      if (!optionControl) {
        optionControl = Array.from(formScope.querySelectorAll('[name^="options"]')).find((el) => String(el.value) === optionValue);
      }

      if (popoutOption) {
        popoutOption.click();
      } else if (optionControl) {
        if (optionControl.type === 'radio') {
          optionControl.click();
        } else {
          optionControl.value = optionValue;
          optionControl.dispatchEvent(new Event('input', { bubbles: true }));
          optionControl.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      const idInput = form.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      return true;
    }

    function chooseSize() {
      if (!syncVariantToProductPage()) return;
      closeModal();
      const form = getProductForm();
      const addButton = form && form.querySelector('[data-add-to-cart]');
      if (addButton) {
        addButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => addButton.focus(), 300);
      }
    }

    async function addToCart() {
      const variant = selectedVariant();
      if (!variant || !variant.available || cartBtn.disabled) return;

      cartBtn.disabled = true;
      const originalText = cartBtn.textContent;
      cartBtn.textContent = 'Adding…';
      scaleStatus.textContent = '';

      try {
        syncVariantToProductPage();
        const mockupBlob = await buildMockupBlob();
        const roomName = roomNameInput ? roomNameInput.value.trim() : '';
        const productForm = getProductForm();
        const formData = productForm ? new FormData(productForm) : new FormData();
        formData.set('id', variant.id);
        if (!formData.get('quantity')) formData.set('quantity', '1');
        if (roomName) formData.set('properties[Room name]', roomName);
        formData.set('properties[_Room visualisation]', mockupBlob, mockupFilename('jpg'));

        const rootPath = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
        const response = await fetch(`${rootPath}cart/add.js`, {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: formData
        });
        const addedItem = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(addedItem.description || addedItem.message || 'Cart add failed');

        const cartResponse = await fetch(`${rootPath}cart.js`, { headers: { 'Accept': 'application/json' } });
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart }, bubbles: true }));
        }

        closeModal();
        const popdown = document.querySelector('[data-product-add-popdown-wrapper]');
        if (popdown) {
          popdown.dispatchEvent(new CustomEvent('theme:cart:popdown', {
            detail: { variant: addedItem },
            bubbles: true
          }));
        }
      } catch (error) {
        console.warn('YDS room visualiser: cart add with room image failed.', error);
        cartBtn.disabled = false;
        cartBtn.textContent = originalText;
        scaleStatus.textContent = 'We could not attach the room image to the cart. Save the mockup, then use the normal Add to cart button.';
        return;
      }

      cartBtn.textContent = originalText;
      cartBtn.disabled = !variant.available;
    }

    openBtn.addEventListener('click', openModal);
    closeBtns.forEach((btn) => btn.addEventListener('click', closeModal));

    document.addEventListener('keydown', (event) => {
      if (modal.hidden) return;
      if (event.key === 'Escape') closeModal();
    });

    fileInputs.forEach((input) => input.addEventListener('change', () => {
      const file = input.files && input.files[0];
      if (file) loadRoom(file);
      input.value = '';
    }));

    scaleInput.addEventListener('input', () => {
      if (state.calibrated) return;
      state.widthPct = clamp(Number(scaleInput.value) || 30, 12, 85);
      renderArtwork();
    });

    rotateInput.addEventListener('input', () => {
      state.rotation = clamp(Number(rotateInput.value) || 0, -15, 15);
      renderArtwork();
    });

    resetBtn.addEventListener('click', resetPlacement);
    nudgeBtns.forEach((btn) => btn.addEventListener('click', () => {
      const direction = btn.dataset.ydsRvNudge;
      const amount = 1.5;
      if (direction === 'up') state.y -= amount;
      if (direction === 'down') state.y += amount;
      if (direction === 'left') state.x -= amount;
      if (direction === 'right') state.x += amount;
      state.x = clamp(state.x, 0, 100);
      state.y = clamp(state.y, 0, 100);
      renderArtwork();
    }));

    startScaleBtn.addEventListener('click', startCalibration);
    clearScaleBtn.addEventListener('click', () => {
      clearCalibration();
      state.widthPct = manualWidthForVariant(selectedVariant());
      renderArtwork();
    });

    stage.addEventListener('pointerdown', (event) => {
      if (!state.calibrationMode) return;
      const rect = stage.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const x = clamp((event.clientX - rect.left) / rect.width, 0, 1);
      const y = clamp((event.clientY - rect.top) / rect.height, 0, 1);
      state.calibrationPoints.push({ x, y });
      if (state.calibrationPoints.length > 2) state.calibrationPoints = [{ x, y }];
      renderCalibration();
      if (state.calibrationPoints.length === 2) completeCalibration();
    });

    artwork.addEventListener('pointerdown', (event) => {
      if (state.calibrationMode) return;
      state.dragPointer = event.pointerId;
      state.dragStartX = event.clientX;
      state.dragStartY = event.clientY;
      state.dragStartStateX = state.x;
      state.dragStartStateY = state.y;
      artwork.setPointerCapture(event.pointerId);
      artwork.classList.add('is-dragging');
      event.preventDefault();
    });

    artwork.addEventListener('pointermove', (event) => {
      if (state.dragPointer !== event.pointerId) return;
      const rect = stage.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      state.x = clamp(state.dragStartStateX + ((event.clientX - state.dragStartX) / rect.width) * 100, 0, 100);
      state.y = clamp(state.dragStartStateY + ((event.clientY - state.dragStartY) / rect.height) * 100, 0, 100);
      renderArtwork();
      event.preventDefault();
    });

    function endDrag(event) {
      if (state.dragPointer !== event.pointerId) return;
      state.dragPointer = null;
      artwork.classList.remove('is-dragging');
      try { artwork.releasePointerCapture(event.pointerId); } catch (_) {}
    }
    artwork.addEventListener('pointerup', endDrag);
    artwork.addEventListener('pointercancel', endDrag);

    chooseBtn.addEventListener('click', chooseSize);
    cartBtn.addEventListener('click', addToCart);
    if (saveBtn) saveBtn.addEventListener('click', downloadMockup);
    if (detectBtn) detectBtn.addEventListener('click', () => runRoomAnalysis(false));
    if (removePhotoBtn) removePhotoBtn.addEventListener('click', removeRoomPhoto);

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => {
        fitStageToViewer();
        renderCalibration();
      });
      resizeObserver.observe(viewerColumn || stage);
    } else {
      window.addEventListener('resize', () => {
        fitStageToViewer();
        renderCalibration();
      }, { passive: true });
    }

    const initialId = getCurrentFormVariantId();
    if (initialId && variants.some((v) => v.id === initialId)) state.selectedVariantId = initialId;
    state.widthPct = manualWidthForVariant(selectedVariant());
    renderSizes();
    renderArtwork();
    renderCalibration();
    // Explicit guide links reuse this product's existing dialog; no file picker opens automatically.
    if (window.location.hash === '#see-it-on-your-wall') {
      Promise.resolve(artworkReadyPromise).then(() => {
        if (root.isConnected && !root.hidden && modal.isConnected) openModal();
      });
    }
  }

  function boot() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', boot);
})();




